import React from "react";
import { motion } from "framer-motion";
import { Shield, Heart, Landmark, Check, Baby, Sparkles, Home, ArrowRight } from "lucide-react";
import { PRODUCTS, KIDS_PRODUCTS, MORTGAGE_PROTECTION } from "../lib/data";

const ICONS = { shield: Shield, heart: Heart, landmark: Landmark };

const ProductCard = ({ product, index }) => {
  const Icon = ICONS[product.icon] || Shield;
  return (
    <motion.article
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9, delay: index * 0.12, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`product-card-${product.id}`}
      className="group relative gradient-border bg-white p-8 sm:p-10 transition-all duration-500 flex flex-col h-full gold-glow"
    >
      {/* Index number bg watermark */}
      <span className="absolute bottom-6 right-6 font-display text-7xl text-gold/8 leading-none pointer-events-none select-none">
        0{index + 1}
      </span>

      <div className="relative flex items-center justify-between mb-8">
        <div className="w-12 h-12 bg-ivory border border-gold/40 flex items-center justify-center group-hover:bg-ink group-hover:border-ink transition-all duration-500">
          <Icon className="w-5 h-5 text-gold group-hover:text-gold-light transition-colors duration-500" />
        </div>
        <span className="eyebrow text-ink/40 text-[10px]">{product.eyebrow}</span>
      </div>

      <h3 className="relative font-display font-light text-3xl sm:text-4xl text-ink leading-tight">
        {product.name}
      </h3>
      <p className="relative mt-4 text-gold text-sm tracking-wide italic font-medium">{product.short}</p>

      <div className="my-8 gold-divider" />

      <div className="space-y-5 flex-grow">
        <div>
          <div className="eyebrow text-ink/50 mb-3 text-[10px]">Definition</div>
          <p className="text-ink/80 leading-relaxed text-[15px] font-light">{product.definition}</p>
        </div>
        <div>
          <div className="eyebrow text-ink/50 mb-3 text-[10px]">Why It Matters</div>
          <p className="text-ink/80 leading-relaxed text-[15px] font-light">{product.importance}</p>
        </div>
      </div>

      <ul className="mt-8 space-y-3 pt-6 border-t border-line">
        {product.bullets.map((b) => (
          <li key={b} className="flex items-start gap-3 text-sm text-ink/75 font-light">
            <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </motion.article>
  );
};

const KidsStrip = () => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9 }}
      className="relative mt-10 lg:mt-12 bg-ink text-ivory border border-gold/40 overflow-hidden"
    >
      {/* Atmospheric gold glow */}
      <div className="absolute -top-20 -left-20 w-80 h-80 bg-gold/15 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-20 -right-20 w-80 h-80 bg-gold-light/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute inset-0 grid-overlay-dark opacity-40" />

      <div className="relative p-8 sm:p-10 lg:p-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Header */}
          <div className="lg:col-span-4">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-gold flex items-center justify-center">
                <Baby className="w-5 h-5 text-ink" />
              </div>
              <span className="eyebrow text-gold">Ages 0 – 17</span>
            </div>
            <h3 className="font-display font-light text-3xl sm:text-4xl leading-[1.1] tracking-tight">
              The greatest gift{" "}
              <span className="italic shimmer">starts early.</span>
            </h3>
            <p className="mt-5 text-ivory/70 text-sm sm:text-base leading-relaxed font-light">
              Children qualify for <span className="text-gold font-medium">Whole Life</span> and{" "}
              <span className="text-gold font-medium">IUL</span> policies at the lowest premiums of
              their lives — guaranteeing lifetime insurability and decades of compounding cash value.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 text-gold text-[10px] eyebrow">
              <Sparkles className="w-3.5 h-3.5" /> Coverage starts at age 0
            </div>
          </div>

          {/* Two child product blocks */}
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-5 lg:gap-6">
            {KIDS_PRODUCTS.map((kp, i) => (
              <motion.div
                key={kp.id}
                initial={{ opacity: 0, y: 15 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.15 + i * 0.1 }}
                data-testid={`kids-card-${kp.id}`}
                className="bg-ivory/[0.04] border border-gold/30 hover:border-gold hover:bg-ivory/[0.06] p-6 transition-all duration-500"
              >
                <h4 className="font-display text-2xl text-gold leading-tight">{kp.name}</h4>
                <p className="mt-3 text-ivory/75 text-sm leading-relaxed font-light">{kp.summary}</p>
                <ul className="mt-5 space-y-2">
                  {kp.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2.5 text-xs text-ivory/65 font-light leading-relaxed">
                      <Check className="w-3.5 h-3.5 text-gold mt-0.5 shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const MortgageStrip = () => {
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.9 }}
      data-testid="mortgage-section"
      className="relative mt-8 lg:mt-10 bg-white border border-line overflow-hidden gradient-border"
    >
      {/* Decorative gold sweep */}
      <div className="absolute -top-32 -right-32 w-96 h-96 bg-gold/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-80 h-80 bg-gold-light/10 rounded-full blur-3xl pointer-events-none" />

      <div className="relative p-8 sm:p-10 lg:p-14">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          {/* Left header */}
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 bg-ink flex items-center justify-center">
                <Home className="w-5 h-5 text-gold" />
              </div>
              <span className="eyebrow text-gold">Homeowner Coverage</span>
            </div>
            <h3 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.05] tracking-tight text-ink">
              {MORTGAGE_PROTECTION.name.split(" ")[0]}{" "}
              <span className="italic gold-text-gradient">{MORTGAGE_PROTECTION.name.split(" ")[1]}</span>
            </h3>
            <p className="mt-5 text-gold text-sm tracking-wide italic font-medium">
              {MORTGAGE_PROTECTION.tagline}
            </p>
            <button
              onClick={() => scrollTo("contact")}
              data-testid="mortgage-cta"
              className="mt-7 group inline-flex items-center gap-2 text-ink hover:text-gold text-xs tracking-[0.25em] uppercase font-semibold transition-colors"
            >
              Request a Mortgage Quote
              <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
            </button>
          </div>

          {/* Right body */}
          <div className="lg:col-span-7">
            <p className="text-ink/80 leading-relaxed text-base sm:text-lg font-light">
              {MORTGAGE_PROTECTION.description}
            </p>
            <p className="mt-5 text-ink/65 leading-relaxed text-sm sm:text-base font-light">
              {MORTGAGE_PROTECTION.longDescription}
            </p>
            <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
              {MORTGAGE_PROTECTION.bullets.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm text-ink/80 font-light leading-relaxed">
                  <Check className="w-4 h-4 text-gold mt-0.5 shrink-0" />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Gold accent corners */}
      <span className="absolute top-0 left-0 w-12 h-px bg-gold" />
      <span className="absolute top-0 left-0 w-px h-12 bg-gold" />
      <span className="absolute bottom-0 right-0 w-12 h-px bg-gold" />
      <span className="absolute bottom-0 right-0 w-px h-12 bg-gold" />
    </motion.div>
  );
};

const Products = () => {
  return (
    <section
      id="products"
      data-testid="products-section"
      className="relative bg-gradient-to-b from-cream/30 via-ivory to-cream/40 py-24 sm:py-32"
    >
      <div className="absolute inset-0 grid-overlay opacity-25" />
      <div className="absolute top-40 left-1/4 w-72 h-72 bg-gold/8 rounded-full blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="max-w-3xl mb-16 sm:mb-20"
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="w-12 h-px bg-gold" />
            <span className="eyebrow text-gold">The Products</span>
          </div>
          <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight text-ink">
            Three pillars. <span className="italic gold-text-gradient">One tailored plan.</span>
          </h2>
          <p className="mt-6 text-ink/65 text-base sm:text-lg leading-relaxed font-light max-w-2xl">
            Every family is different. Below are the three foundational types of life insurance —
            explained simply, so you can decide which best protects what matters most.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {PRODUCTS.map((p, i) => (<ProductCard key={p.id} product={p} index={i} />))}
        </div>

        {/* Horizontal box for Kids products */}
        <KidsStrip />

        {/* Mortgage Protection */}
        <MortgageStrip />
      </div>
    </section>
  );
};

export default Products;
